/**
 * quote-pricing.ts — THE canonical money-math module for quotes + proposals.
 *
 * One definition of every dollar figure the system shows or persists.
 * Every other surface (quote builder, proposal render context, proposal
 * snapshots, playground, PDF payloads, version pinning) is a thin wrapper
 * over the helpers here. Added in the 2026-06-09 proposal-system audit
 * remediation (Parts A2 / C-H4 / C-M11 / D-M1 / D-M2 / F).
 *
 * ── CANONICAL CONVENTIONS ──────────────────────────────────────────────
 *
 * 1. `scope.subtotal` (the persisted field, and anything labelled
 *    "subtotal") = ONE-TIME items + labour, ex-GST. Recurring items are
 *    EXCLUDED — they are reported separately as a monthly figure and are
 *    never folded into a one-time number, never charged one-time GST.
 *
 * 2. Per-item money is ALWAYS recomputed as `unitPrice × quantity`
 *    (via `itemTotal`). The stored `totalPrice` field is an untrusted
 *    display cache — BP imports / legacy writers left stale values that
 *    diverged from unitPrice × qty. Use `normalizeQuoteItem` before any
 *    persist to bring the cache back in line.
 *
 * 3. GST applies to the one-time subtotal only. Recurring figures are
 *    quoted ex-GST (matching how the AU market quotes recurring revenue).
 *
 * 4. `annualContractValue` is FULLY inc-GST:
 *    `oneTimeTotal + 12 × monthlyRecurring × (1 + gstRate)`.
 *    (Pre-audit it mixed inc-GST one-time with ex-GST recurring — D-M1.)
 *
 * 5. Unknown / corrupt `laborType` values price at $0 with a console.warn
 *    naming the bad value — never NaN (C-M11).
 *
 * 6. In a `recurringSelectionMode: 'one'` (pick-one) scope, the OPTIONAL
 *    recurring items are mutually exclusive — only the DEFAULT-selected
 *    plan counts toward the monthly figure. Mirrors `seedExclusiveDefaults`
 *    in the customer viewer so the office's monthly total matches what the
 *    customer sees + signs by default. Without it a "pick one" scope
 *    double-counts competing plans in the monthly total.
 *
 * This module is PURE — no Firebase imports — so it's consumable from
 * render contexts, tests, and (eventually) shared with functions/.
 */

import type { PlaygroundQuoteItem } from '@/types/schema';
import type { Deal } from '@/types/theo-workflow.types';
import { DEFAULT_QUOTE_LABOUR_RATES } from '@/utils/labourRates';
import { sanitizeGstRate as sharedSanitizeGstRate } from '@shared/money/tax';
import { roundMoney, toFiniteMoney } from '@shared/money/round';
import { netLineTotal, sanitizeDiscountPercent as sharedSanitizeDiscountPercent } from '@shared/money/discount';
import { isRecurringBillingType } from '@shared/domain/billing-type';
import {
  defaultOptionId,
  filterScopesForOption,
  minOptionPricing,
  optionColumns,
  optionsDifferInMonthly,
  withoutDefaultOffLines,
} from '@shared/proposals/options';

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────

/**
 * AU GST. Single source for every 0.1 / 1.1 literal in the quote/proposal
 * money paths.
 *
 * TODO(saas-readiness): GST hardcoded at 0.10 — should read
 * tenant.business.defaultTaxRate. Deferred as a dedicated whole-chain money
 * task: GST is computed in parallel server-side (functions/), so client +
 * server must convert together or quote/invoice totals diverge for a non-AU
 * tenant. Until then this constant is the ONE place the literal lives;
 * callers with tenant context should pass `tenant.business.defaultTaxRate`
 * as the explicit `gstRate` argument.
 */
export const DEFAULT_GST_RATE = 0.10;

/**
 * Quote labour rates (hourly, ex GST) — re-export of the one true table.
 *
 * TODO(saas-readiness): callers should switch to
 * `getQuoteLabourRates(tenant)` from src/utils/labourRates.ts so per-tenant
 * overrides (tenant.business.labourRates.quote) take effect. Deferred
 * whole-chain money task (server-side mirrors must move together). See
 * SaaS-readiness audit (May 2026).
 */
export const QUOTE_LABOUR_RATES: Record<string, number> = {
  ...DEFAULT_QUOTE_LABOUR_RATES,
};

// ────────────────────────────────────────────────────────────────────────────
// Primitives
// ────────────────────────────────────────────────────────────────────────────

/**
 * Round to cents + untrusted-money coercion — Wave 1: implementations live
 * in shared/src/money/round.ts (one source for client + server), imported +
 * re-exported here so this module's many importers are untouched.
 */
export { roundMoney, toFiniteMoney };

/**
 * Sanitize an untrusted GST/tax rate into a valid DECIMAL rate.
 *
 * The rate reaches money math from tenant config (`business.defaultTaxRate`)
 * and from frozen proposal snapshots (`content.pricing.gstRate`) — both of
 * which can carry garbage from console edits or legacy writers. Two failure
 * shapes have CUSTOMER-VISIBLE ×10 consequences when they reach a
 * `value * (1 + gstRate)` gross-up:
 *
 *   - string '0.1'  → `1 + '0.1'` string-concats to '10.1' → ×10.1
 *   - percent 10    → `1 + 10` = 11 → ×11
 *
 * Healing rules — Wave 1: the implementation lives in the SHARED module
 * shared/src/money/tax.ts (one source of truth, client + server; the
 * functions bundle inlines the same code). This wrapper adds the
 * client-side diagnostic console.warns, preserving the exact messages:
 *   - numeric strings parse (with a console.warn)
 *   - decimals in [0, 1] pass through
 *   - values in (1, 100] are treated as percent-form and divided by 100
 *   - everything else (negative, > 100, NaN, non-numeric) → fallback
 */
export function sanitizeGstRate(
  rate: unknown,
  fallback: number = DEFAULT_GST_RATE,
): number {
  return sharedSanitizeGstRate(rate, fallback, (message) =>
    console.warn(`[quote-pricing] ${message}`),
  );
}

/**
 * Sanitize an untrusted discount percentage into a valid number in [0, 100].
 *
 * The proposal-level discount (`deal.quoteDiscountPercent`) is an OPTIONAL
 * percentage the office takes off the one-time (labour + items) subtotal, and
 * the customer sees it on the proposal + is invoiced the discounted amount.
 * It reaches money math from the deal doc / frozen snapshot, so it can carry
 * garbage (string, negative, > 100). Rules:
 *   - non-numeric / null / undefined / ≤ 0 → 0 (no discount)
 *   - numeric strings parse
 *   - clamped to a 100% ceiling (never invert a total)
 */
export function sanitizeDiscountPercent(pct: unknown): number {
  // Wave 1: delegates to shared/src/money/discount.ts — same rules, one impl.
  return sharedSanitizeDiscountPercent(pct);
}

/**
 * Tolerant recurring-billing check — THE one classifier every money path
 * must share. Non-canonical snapshots (BP / signed-import paths that
 * bypassed scopeToRender) carry casings like 'Recurring' / 'recurring ';
 * splitting an item's CLASSIFICATION between a strict check (totals) and a
 * tolerant check (render) showed the customer an item under "Ongoing
 * services" while its money landed in the one-time bucket.
 */
export { isRecurringBillingType } from '@shared/domain/billing-type';

/**
 * Resolve the hourly rate for a labour type. Unknown / legacy / corrupt
 * values price at $0 with a console.warn naming the bad value (C-M11 —
 * previously `LABOUR_RATES[x] * hours` produced NaN that flowed through
 * every total and was persisted).
 */
export function labourRateFor(laborType: string | undefined | null): number {
  if (!laborType) return 0;
  const rate = QUOTE_LABOUR_RATES[laborType];
  if (rate === undefined) {
     
    console.warn(
      `[quote-pricing] Unknown laborType "${laborType}" — pricing labour at $0/hr. ` +
        `Valid values: ${Object.keys(QUOTE_LABOUR_RATES).join(', ')}`,
    );
    return 0;
  }
  return rate;
}

/**
 * The shape both `QuoteScope.items[]` entries and proposal render line
 * items satisfy — everything itemTotal needs.
 */
export interface PriceableItem {
  unitPrice?: number;
  quantity: number;
  billingType?: string;
  /** Untrusted display cache — consulted ONLY when unitPrice is absent. */
  totalPrice?: number;
  /** Optional per-line discount % (0–100) off the list line total. */
  discountPercent?: number;
  /**
   * Customer-side optional flag. Needed to identify the mutually-exclusive
   * recurring plans in a `recurringSelectionMode: 'one'` scope (convention 6).
   * Absent items are treated as required (always counted).
   */
  isOptional?: boolean;
  /**
   * Starting state of an optional item's toggle. In a pick-one scope, the
   * FIRST optional recurring plan whose `optionalDefault` isn't `false` is the
   * one counted by default (mirrors the customer viewer's seedExclusiveDefaults).
   */
  optionalDefault?: boolean;
}

/**
 * Resolve the effective PER-UNIT price for display + math.
 *
 * Canonical source is `unitPrice` (coerced through toFiniteMoney so a
 * string-typed number from an import heals instead of NaN-ing). When
 * unitPrice is ABSENT entirely — import paths that only ever wrote the
 * `totalPrice` cache — fall back to totalPrice ÷ quantity so the customer
 * sees the figure the office reviewed instead of $0. An explicit
 * `unitPrice: 0` is respected as a genuine free item (no fallback).
 */
export function resolveUnitPrice(item: {
  unitPrice?: unknown;
  totalPrice?: unknown;
  quantity?: unknown;
}): number {
  const unit = toFiniteMoney(item.unitPrice);
  if (unit !== null) return unit;
  const cached = toFiniteMoney(item.totalPrice);
  if (cached === null) return 0;
  const qty = toFiniteMoney(item.quantity);
  if (qty !== null && qty > 0) return cached / qty;
  return cached;
}

/**
 * Per-item DISCOUNT % (0–100), sanitized. 0 when absent — so a plain item
 * is byte-identical to the pre-per-item-discount math.
 */
export function itemDiscountPercent(item: { discountPercent?: unknown }): number {
  return sanitizeDiscountPercent(item.discountPercent);
}

/**
 * LIST line total — `unitPrice × quantity`, rounded to cents, BEFORE any
 * per-item discount. This is the "was" figure shown struck through to the
 * customer + office. When `unitPrice` is absent (import paths that only set
 * the cache) the cache IS the only price we have — used flat.
 */
export function itemListTotal(item: PriceableItem): number {
  const unit = toFiniteMoney(item.unitPrice);
  if (unit !== null) {
    const qty = toFiniteMoney(item.quantity) ?? 0;
    return roundMoney(unit * qty);
  }
  const cached = toFiniteMoney(item.totalPrice);
  return cached !== null ? roundMoney(cached) : 0;
}

/**
 * CANONICAL per-item money — the list total NET of the per-item discount,
 * rounded to cents. Everything that sums an item (scope subtotals, GST,
 * billing, the customer's "now" price) goes through here, so the per-item
 * discount flows automatically without per-site changes. `itemListTotal`
 * is the pre-discount figure.
 *
 * Cache exception (2026-07): when `unitPrice` is missing (import paths that
 * only set the `totalPrice` cache), the cache IS the only price we have and
 * it's treated as the final money — the discount is NEVER re-applied to it
 * (such items don't carry `discountPercent` anyway). Returning 0 here made
 * the office's line-item editor show $33 while the snapshot computed $0.
 */
export function itemTotal(item: PriceableItem): number {
  const unit = toFiniteMoney(item.unitPrice);
  if (unit === null) {
    const cached = toFiniteMoney(item.totalPrice);
    return cached !== null ? roundMoney(cached) : 0;
  }
  const qty = toFiniteMoney(item.quantity) ?? 0;
  // Wave 1: the list→net penny math is the shared netLineTotal — the same
  // implementation the server's invoice lines use (resolveItemDiscount).
  return netLineTotal(unit, qty, item.discountPercent).netExGst;
}

/** Per-item discount $ (list − net), rounded. 0 when no per-item discount. */
export function itemDiscountAmount(item: PriceableItem): number {
  return roundMoney(itemListTotal(item) - itemTotal(item));
}

/**
 * Return a copy of the item with its `totalPrice` cache recomputed from
 * the canonical formula. Run this before persisting items so the display
 * cache can't drift from unitPrice × quantity.
 */
export function normalizeQuoteItem(item: PlaygroundQuoteItem): PlaygroundQuoteItem {
  return { ...item, totalPrice: itemTotal(item) };
}

function isRecurring(item: PriceableItem): boolean {
  return isRecurringBillingType(item.billingType);
}

// ────────────────────────────────────────────────────────────────────────────
// Scope-level pricing
// ────────────────────────────────────────────────────────────────────────────

export interface ScopePricing {
  /** Σ itemTotal over one-time items — ex-GST. */
  oneTimeSubtotal: number;
  /**
   * Σ itemTotal over recurring items — ex-GST, per month. In a
   * `recurringSelectionMode: 'one'` scope the mutually-exclusive optional
   * recurring plans are collapsed to the single DEFAULT-selected one
   * (convention 6) so a pick-one scope reports one plan, not the sum.
   */
  monthlySubtotal: number;
  /** labour rate × hours — ex-GST. NaN-guarded (unknown laborType → $0). */
  labourTotal: number;
  /**
   * THE canonical `scope.subtotal`: oneTimeSubtotal + labourTotal,
   * ex-GST. Recurring EXCLUDED (convention 1).
   */
  subtotal: number;
}

/** The fields priceScope needs — satisfied by QuoteScope. */
export interface PriceableScope {
  items: PriceableItem[];
  laborType?: string;
  laborHours?: number;
  /** Directly-set labour cash (ex-GST). When > 0, overrides the
   *  legacy laborType × laborHours rate calc. */
  laborAmount?: number;
  /**
   * How the customer may pick the OPTIONAL recurring plans in this scope.
   * `'one'` = mutually exclusive (pick-one) → only the default-selected plan
   * counts toward the monthly figure (convention 6). `'any'` / undefined =
   * independent, every recurring item counts.
   */
  recurringSelectionMode?: 'any' | 'one';
}

/**
 * The OPTIONAL recurring items in a `recurringSelectionMode: 'one'` scope
 * that must NOT count toward the default monthly total — i.e. every
 * mutually-exclusive plan EXCEPT the one selected by default.
 *
 * A pick-one scope's optional recurring plans are mutually exclusive: the
 * customer chooses a single plan. The default view seeds exactly one — the
 * FIRST optional recurring plan whose `optionalDefault` isn't `false` (none,
 * if every plan is default-off). This mirrors `seedExclusiveDefaults` in the
 * customer viewer (useScopeItemSelections) so the office monthly figure
 * matches what the customer sees + signs by default. Without it a "pick one"
 * scope double-counts competing plans in the monthly total (the reported bug).
 *
 * Non-optional recurring items are never part of the exclusive set — they are
 * always counted (mirrors `exclusiveRecurringIds`, which filters on
 * `isOptional`). Returns a REFERENCE set (works on items that may lack a
 * stable id); empty for non-'one' scopes or scopes with no optional recurring
 * items, so callers can branch uniformly on `.has(item)`.
 */
export function nonDefaultExclusiveRecurring(
  scope: PriceableScope,
): Set<PriceableItem> {
  const excluded = new Set<PriceableItem>();
  if (scope.recurringSelectionMode !== 'one') return excluded;
  const optionalRecurring = scope.items.filter(
    (i) => isRecurring(i) && i.isOptional,
  );
  if (optionalRecurring.length === 0) return excluded;
  // Kept = the first default-on optional recurring plan (undefined when every
  // plan is default-off → the customer default selects NONE, so all are
  // excluded). Everything else in the exclusive set is dropped from the total.
  const kept = optionalRecurring.find((i) => i.optionalDefault !== false);
  for (const item of optionalRecurring) {
    if (item !== kept) excluded.add(item);
  }
  return excluded;
}

/**
 * Canonical scope pricing. Everything that prices a scope — the builder,
 * render context, version pinning, PDF payloads — goes through here.
 */
export function priceScope(scope: PriceableScope): ScopePricing {
  // Pick-one scopes: the mutually-exclusive optional recurring plans collapse
  // to the single default-selected one so the monthly figure doesn't
  // double-count competing plans (convention 6). Empty set (no lookups cost)
  // for the overwhelmingly common non-'one' scope.
  const excludedRecurring = nonDefaultExclusiveRecurring(scope);

  let oneTime = 0;
  let monthly = 0;
  for (const item of scope.items) {
    if (isRecurring(item)) {
      if (excludedRecurring.has(item)) continue;
      monthly += itemTotal(item);
    } else {
      oneTime += itemTotal(item);
    }
  }

  let labourTotal = 0;
  // toFiniteMoney (not a bare typeof check) so a string-typed laborAmount
  // ("500" from a console edit / CSV import) heals here the SAME way the
  // server's scopeLabourToCanonicalItem heals it via pickNumber — a
  // number-only check priced that scope's labour at $0 on the signed
  // proposal while the invoice + Xero push billed the $500.
  const laborAmount = toFiniteMoney(scope.laborAmount);
  if (laborAmount !== null && laborAmount > 0) {
    // New model: a directly-set cash amount wins (the customer-facing
    // "Solution installation" line price).
    labourTotal = roundMoney(laborAmount);
  } else if (scope.laborType && scope.laborHours && scope.laborHours > 0) {
    // Legacy fallback: rate × hours.
    const hours = Number.isFinite(scope.laborHours) ? scope.laborHours : 0;
    labourTotal = roundMoney(labourRateFor(scope.laborType) * hours);
  }

  const oneTimeSubtotal = roundMoney(oneTime);
  const monthlySubtotal = roundMoney(monthly);
  return {
    oneTimeSubtotal,
    monthlySubtotal,
    labourTotal,
    subtotal: roundMoney(oneTimeSubtotal + labourTotal),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Labour summary (for scheduling)
// ────────────────────────────────────────────────────────────────────────────

/** The fields summariseScopeLabour reads — satisfied by QuoteScope. */
export interface LabourSummarisableScope {
  laborType?: string;
  laborHours?: number;
  laborTechCount?: number;
}

export interface QuotedLabourSummary {
  /** Σ wall-clock labour hours across scopes. */
  hours: number;
  /**
   * Crew size: the MAX techs any single scope needs (the crew works the
   * job together, so the visit needs the biggest crew any scope calls
   * for — not the sum). Inferred from `laborType` when `laborTechCount`
   * is unset ('1-tech' → 1, '2-techs' / '2-senior-techs' → 2).
   */
  techCount: number;
}

/**
 * Roll a deal's quote scopes up into the labour the office QUOTED — total
 * hours + crew size — so the scheduler can PRE-FILL the install duration +
 * tech count instead of re-guessing, and see what was quoted vs scheduled.
 * Pure; safe on undefined / empty. Display + scheduling aid only — the
 * billable labour figure is the scope's `laborAmount` (see priceScope).
 */
export function summariseScopeLabour(
  scopes: ReadonlyArray<LabourSummarisableScope> | undefined | null,
): QuotedLabourSummary {
  let hours = 0;
  let techCount = 0;
  for (const scope of scopes ?? []) {
    const h =
      typeof scope.laborHours === 'number' && Number.isFinite(scope.laborHours)
        ? scope.laborHours
        : 0;
    if (h > 0) hours += h;
    let tc =
      typeof scope.laborTechCount === 'number' &&
      Number.isFinite(scope.laborTechCount)
        ? scope.laborTechCount
        : 0;
    if (tc <= 0 && scope.laborType) {
      tc = scope.laborType === '1-tech' ? 1 : 2;
    }
    if (tc > techCount) techCount = tc;
  }
  return { hours: roundMoney(hours), techCount };
}

// ────────────────────────────────────────────────────────────────────────────
// Quote-level pricing
// ────────────────────────────────────────────────────────────────────────────

export interface QuotePricing {
  /** Σ scope subtotals (one-time items + labour) — ex-GST, BEFORE discount. */
  oneTimeSubtotal: number;
  /** Discount % applied to the one-time subtotal (0–100; 0 = none). */
  discountPercent: number;
  /** Discount $ off the one-time subtotal — ex-GST. 0 when no discount. */
  discountAmount: number;
  /** GST on the DISCOUNTED one-time subtotal. Recurring is never GST'd here. */
  gst: number;
  /**
   * (oneTimeSubtotal − discountAmount) + gst — inc-GST, AFTER discount.
   * The final one-off figure the customer pays / signs / is invoiced.
   */
  oneTimeTotal: number;
  /**
   * inc-GST total of the FULL (undiscounted) one-time subtotal — the
   * "list price" the customer sees struck-through when a discount applies.
   * Computed with the SAME two-step round (subtotal + roundedGST) as
   * `oneTimeTotal`, so when `discountPercent === 0` this === `oneTimeTotal`
   * exactly. Consumers (the "you save" hero, the sticky-CTA saving line)
   * read this instead of re-grossing `oneTimeSubtotal × (1 + gstRate)`,
   * which double-rounds and can drift a cent.
   */
  oneTimeTotalBeforeDiscount: number;
  /**
   * Σ recurring item totals across all scopes — ex-GST, per month (NOT
   * discounted). Pick-one scopes contribute only their default-selected plan
   * (convention 6), so a "monthly" figure never double-counts competing plans.
   */
  monthlyRecurring: number;
  /**
   * 12-month contract value, FULLY inc-GST (convention 4):
   * `oneTimeTotal + 12 × monthlyRecurring × (1 + gstRate)`.
   */
  annualContractValue: number;
}

/**
 * Canonical quote-level pricing across scopes.
 *
 * @param gstRate Defaults to DEFAULT_GST_RATE. Callers with tenant
 *   context should pass `tenant.business.defaultTaxRate`.
 * @param discountPercent Optional one-time discount % (0–100). Applied to the
 *   one-time (items + labour) subtotal ex-GST BEFORE GST — so GST + total are
 *   on the discounted amount, exactly what the customer pays. Monthly
 *   recurring is NOT discounted. `discountPercent === 0` (the default) is
 *   byte-identical to the pre-discount math.
 */
export function priceQuote(
  scopes: PriceableScope[],
  gstRate: number = DEFAULT_GST_RATE,
  discountPercent: number = 0,
): QuotePricing {
  // Harden the rate at the money boundary — a percent-form or
  // string-typed rate reaching the `1 + gstRate` gross-ups below
  // multiplies customer-facing figures ~×10 (see sanitizeGstRate).
  gstRate = sanitizeGstRate(gstRate);
  const pct = sanitizeDiscountPercent(discountPercent);
  let oneTimeSubtotal = 0;
  let monthlyRecurring = 0;
  for (const scope of scopes) {
    const p = priceScope(scope);
    oneTimeSubtotal += p.subtotal;
    monthlyRecurring += p.monthlySubtotal;
  }
  oneTimeSubtotal = roundMoney(oneTimeSubtotal);
  monthlyRecurring = roundMoney(monthlyRecurring);

  const discountAmount = roundMoney(oneTimeSubtotal * (pct / 100));
  const discountedSubtotal = roundMoney(oneTimeSubtotal - discountAmount);
  const gst = roundMoney(discountedSubtotal * gstRate);
  const oneTimeTotal = roundMoney(discountedSubtotal + gst);

  // "List price" — the inc-GST one-time total BEFORE the discount. Same
  // two-step round as `oneTimeTotal` (subtotal + rounded-GST) so at
  // discountPercent === 0 the two are byte-identical.
  const gstOnFull = roundMoney(oneTimeSubtotal * gstRate);
  const oneTimeTotalBeforeDiscount = roundMoney(oneTimeSubtotal + gstOnFull);

  return {
    oneTimeSubtotal,
    discountPercent: pct,
    discountAmount,
    gst,
    oneTimeTotal,
    oneTimeTotalBeforeDiscount,
    monthlyRecurring,
    annualContractValue: roundMoney(
      oneTimeTotal + 12 * monthlyRecurring * (1 + gstRate),
    ),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Customer-facing monthly figure (Proposal Builder V2)
// ────────────────────────────────────────────────────────────────────────────

/** A line `monthlyIncTaxTotal` reads: a priceable item plus its recurring interval. */
export interface MonthlyPriceableItem extends PriceableItem {
  recurringInterval?: string;
}

/**
 * The monthly figure a CUSTOMER sees, INC tax (billing skill basis map:
 * recurring lines are stored ex-GST and grossed up at display): Σ over the
 * MONTHLY recurring lines of `itemTotal × (1 + gstRate)`, each line rounded
 * to the cent — the per-line maths of the V2 document's Ongoing services
 * table, so an option card (R8) never disagrees with the table. Yearly lines
 * are not monthly; one-time lines are skipped. The caller narrows the lines
 * (to an option, without the unticked optionals). `monthlyRecurring` (ex tax,
 * every recurring line) is unchanged. One definition for the V2 document
 * model (`monthlyIncTax`) and the V2 send freeze (`pricingByOption[*]
 * .monthlyTotal`).
 */
export function monthlyIncTaxTotal(items: Iterable<MonthlyPriceableItem>, gstRate: number): number {
  const rate = sanitizeGstRate(gstRate);
  let total = 0;
  for (const item of items) {
    if (!isRecurring(item) || item.recurringInterval === 'yearly') continue;
    total += roundMoney(itemTotal(item) * (1 + rate));
  }
  return roundMoney(total);
}

// ────────────────────────────────────────────────────────────────────────────
// Deal-level pricing (Proposal Builder V2 aware)
// ────────────────────────────────────────────────────────────────────────────

/** `priceQuote`'s figures for a deal, plus how many V2 options it has. */
export interface DealQuotePricing extends QuotePricing {
  /** `deal.proposalV2.options.length` (0 when none). Office surfaces show "from …" when > 1. */
  optionCount: number;
  /** The option the figures are for — set only for a V2 deal with options. */
  optionId?: string;
}

/**
 * The office's headline quote figures for a deal (status card, quote
 * summary, timeline).
 *
 * - V1 deal (no `proposalBuilderVersion: 2`): exactly `priceQuote` over
 *   every scope — byte-identical to calling it directly.
 * - V2 deal: the DEFAULT option's figures (recommended ?? first), i.e.
 *   `priceQuote` over the scopes narrowed to that option's items. Scopes
 *   and labour are never filtered. No options → every item (same as V1).
 *   R1 §3.5: without the optional lines that START unticked
 *   (`optionalDefault === false`) — the customer's total until they tick
 *   one; the V2 send freezes the same figures.
 *
 * Does NOT change `priceScope` / `priceQuote` — the persisted
 * `scope.subtotal` keeps counting ALL items (H4). GST is the caller's
 * tenant rate; the discount is `deal.quoteDiscountPercent`.
 */
export function priceDealQuote(
  deal: Pick<Deal, 'quoteScopes' | 'quoteDiscountPercent' | 'proposalBuilderVersion' | 'proposalV2'>,
  gstRate: number,
): DealQuotePricing {
  const v2 = deal.proposalBuilderVersion === 2;
  const scopes = v2 ? withoutDefaultOffLines(deal.quoteScopes ?? []) : (deal.quoteScopes ?? []);
  const optionCount = deal.proposalV2?.options?.length ?? 0;
  const optionId = v2 ? defaultOptionId(deal.proposalV2) : undefined;
  const priced = optionId !== undefined ? filterScopesForOption(scopes, optionId) : scopes;
  return {
    ...priceQuote(priced, gstRate, deal.quoteDiscountPercent),
    optionCount,
    ...(optionId !== undefined ? { optionId } : {}),
  };
}

/**
 * R1 §3.5 — the `deal.value` a V2 save writes. The V1 builder writes no
 * `value` on save; everywhere the field IS written (acceptance —
 * `onProposalAccepted`'s `dealItems.total`; the PDF / scanner imports) it is
 * the one-time total INC GST after the discount — the figure the pipeline
 * shows as "inc GST" and the deposit rule compares. V2 writes that same
 * figure for the draft: `priceDealQuote` for a V2 deal (the default
 * option's, without the optional lines that start unticked).
 */
export function v2DealValue(
  draft: { scopes: Deal['quoteScopes']; v2: Deal['proposalV2']; discountPercent: number },
  gstRate: number,
): number {
  return priceDealQuote(
    { quoteScopes: draft.scopes, quoteDiscountPercent: draft.discountPercent, proposalBuilderVersion: 2, proposalV2: draft.v2 },
    gstRate,
  ).oneTimeTotal;
}

/** The office's headline one-off figure for a deal (S7 — "from $X" with options). */
export interface DealHeadlineTotal {
  /** inc-GST one-time total: the cheapest option's when `from`, else `priceDealQuote`'s. */
  oneTimeTotal: number;
  /** True for a V2 deal with more than one option — label the figure "from". */
  from: boolean;
  /** The cheapest option (set only when `from`). */
  optionId?: string;
  /**
   * The figures `oneTimeTotal` comes from: the cheapest option's when `from`,
   * else `pricing`. Its `monthlyRecurring` is the monthly to show beside a
   * "from" figure — the SAME option's (R8), never the default option's.
   */
  headline: QuotePricing;
  /** `priceDealQuote` (the default option's figures, + `optionCount`). */
  pricing: DealQuotePricing;
  /**
   * R8 — `from`, and the options differ in their monthly figure (shared
   * `optionsDifferInMonthly`, the customer emails' rule): the "from" package
   * then reads "from $X + $Y / month", Y = `headline.monthlyRecurring`
   * (grossed up for display). False otherwise — options that agree read as
   * before.
   */
  monthlyDiffers: boolean;
}

/**
 * The headline one-off figure the office cards show for a deal (status
 * card, quote summary, timeline — Proposal Builder V2, S7). With more than
 * one option it is the MINIMUM across the options' inc-GST totals (each
 * option = `priceQuote` over the scopes narrowed to it, the deal discount
 * applied) and is shown as "from $X" — the same rule the customer emails
 * use (`minOptionPricing`, shared with functions/). Otherwise exactly
 * `priceDealQuote(deal).oneTimeTotal`. R8: a monthly shown beside "from $X"
 * is `headline.monthlyRecurring` (that same option's); `monthlyDiffers` says
 * whether the options disagree on it.
 */
export function priceDealQuoteFrom(
  deal: Pick<Deal, 'quoteScopes' | 'quoteDiscountPercent' | 'proposalBuilderVersion' | 'proposalV2'>,
  gstRate: number,
): DealHeadlineTotal {
  const pricing = priceDealQuote(deal, gstRate);
  const ids = deal.proposalBuilderVersion === 2 ? optionColumns(deal.proposalV2) : [];
  if (ids.length <= 1) return { oneTimeTotal: pricing.oneTimeTotal, from: false, pricing, headline: pricing, monthlyDiffers: false };
  const scopes = withoutDefaultOffLines(deal.quoteScopes ?? []); // R1 §3.5, as priceDealQuote
  const byOption: Record<string, QuotePricing> = {};
  for (const id of ids) byOption[id] = priceQuote(filterScopesForOption(scopes, id), gstRate, deal.quoteDiscountPercent);
  const min = minOptionPricing(byOption);
  return min
    ? {
        oneTimeTotal: min.oneTimeTotal,
        from: true,
        optionId: min.optionId,
        pricing,
        headline: min.pricing,
        monthlyDiffers: optionsDifferInMonthly(byOption),
      }
    : { oneTimeTotal: pricing.oneTimeTotal, from: false, pricing, headline: pricing, monthlyDiffers: false };
}

// ────────────────────────────────────────────────────────────────────────────
// Live (selection-aware) discount application
// ────────────────────────────────────────────────────────────────────────────

/**
 * A self-consistent one-time pricing block derived from a LIVE
 * (selection-aware) UNDISCOUNTED inc-GST total. Every field reconciles:
 *   - `oneTimeSubtotal − discountAmount + gst === oneTimeTotal` (ex-GST view)
 *   - `oneTimeTotalBeforeDiscount − savingIncGst === oneTimeTotal` (inc-GST view)
 */
export interface LiveDiscountedOneTime {
  /** ex-GST list subtotal (pre-discount), recovered from the inc-GST total. */
  oneTimeSubtotal: number;
  /** Discount % applied (0–100; 0 = none). */
  discountPercent: number;
  /** ex-GST discount $ (list subtotal − discounted subtotal). */
  discountAmount: number;
  /** GST on the DISCOUNTED subtotal. */
  gst: number;
  /** inc-GST one-time total AFTER the discount — what the customer signs. */
  oneTimeTotal: number;
  /** inc-GST one-time total BEFORE the discount — the struck-through "list". */
  oneTimeTotalBeforeDiscount: number;
  /** inc-GST saving = before − after. */
  savingIncGst: number;
}

/**
 * Build a self-consistent discounted one-time block from a LIVE
 * UNDISCOUNTED inc-GST total.
 *
 * The customer viewer computes selection deltas (opt-in/out, qty steppers,
 * tiers) at FULL list price in INC-GST space, then applies the proposal-wide
 * % discount as a single final scale. This helper turns that one inc-GST list
 * total into the full set of figures the render sections need — pre-discount
 * ex-GST subtotal, ex-GST discount $, GST on the discounted amount, and both
 * inc-GST totals — so the "you save" hero, the legacy pricing breakdown, and
 * the sticky CTA all reconcile to the penny.
 *
 * The ex-GST fields are RECOVERED from the inc-GST totals (÷ 1+gstRate) rather
 * than re-summed from scopes, precisely so `subtotal − discount + gst === total`
 * holds against the very inc-GST total the customer is signing against — the
 * canonical invoice re-prices the ACCEPTED line items server-side, so a sub-cent
 * divergence between this display recovery and a fresh ex-GST recompute is both
 * expected and immaterial (absorbed by the ≤$1 reconciliation guard).
 */
export function applyDiscountToLiveIncGstTotal(
  incGstUndiscounted: number,
  gstRate: number = DEFAULT_GST_RATE,
  discountPercent: number = 0,
): LiveDiscountedOneTime {
  gstRate = sanitizeGstRate(gstRate);
  const pct = sanitizeDiscountPercent(discountPercent);
  const before = roundMoney(Math.max(0, incGstUndiscounted));
  const oneTimeTotal = roundMoney(before * (1 - pct / 100));
  const oneTimeSubtotal = roundMoney(before / (1 + gstRate));
  const discountedSubtotal = roundMoney(oneTimeTotal / (1 + gstRate));
  const discountAmount = roundMoney(oneTimeSubtotal - discountedSubtotal);
  const gst = roundMoney(oneTimeTotal - discountedSubtotal);
  return {
    oneTimeSubtotal,
    discountPercent: pct,
    discountAmount,
    gst,
    oneTimeTotal,
    oneTimeTotalBeforeDiscount: before,
    savingIncGst: roundMoney(before - oneTimeTotal),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Content guards (A2 — zero-scope proposal create/send protection)
// ────────────────────────────────────────────────────────────────────────────

/**
 * The shape both QuoteScope[] and ProposalContentSnapshot.scopes
 * (ProposalScopeRender[]) satisfy for the "does this quote actually
 * contain anything sellable?" check.
 */
interface ContentCheckScope {
  items: unknown[];
  laborHours?: number;
  laborAmount?: number;
  labour?: { hours?: number; total?: number } | null;
}

/**
 * True when the scopes carry at least one line item OR at least one
 * scope with billable labour. Used by `createProposalFromQuote` /
 * `sendProposal` to refuse to freeze an empty (or sample-substituted)
 * quote into a customer-facing snapshot.
 */
export function quoteScopesHaveContent(
  scopes: ContentCheckScope[] | null | undefined,
): boolean {
  if (!scopes || scopes.length === 0) return false;
  return scopes.some(
    (s) =>
      (s.items?.length ?? 0) > 0 ||
      (s.laborAmount ?? 0) > 0 ||
      (s.laborHours ?? 0) > 0 ||
      (s.labour?.hours ?? 0) > 0 ||
      (s.labour?.total ?? 0) > 0,
  );
}
